import { motion } from "framer-motion";

const WARM = "#C9A24B";
const GREEN = "#6B8F71";
const INK = "#1A1A1A";

// points of view around one situation — POVs shift by system, not fixed roles
const povs = [
  { label: "POV 1", angle: -90, color: WARM, shape: "triangle" },
  { label: "POV 2", angle: 0, color: GREEN, shape: "square" },
  { label: "POV 3", angle: 90, color: WARM, shape: "circle" },
  { label: "POV 4", angle: 180, color: GREEN, shape: "diamond" },
];

const C = { x: 160, y: 160 };
const R_EYE = 62;
const R_PROJ = 90;
const R_LINE = 108;
const R_LABEL = 134;

const EASE = [0.22, 1, 0.36, 1];
const CONTAINER_INITIAL = { opacity: 0, y: 30 };
const CONTAINER_IN_VIEW = { opacity: 1, y: 0 };
const CONTAINER_TRANSITION = { duration: 0.9, ease: EASE };
const CONTAINER_VIEWPORT = { once: true };
const LINE_INITIAL = { pathLength: 0, opacity: 0 };
const LINE_IN_VIEW = { pathLength: 1, opacity: 0.7 };

const Eye = ({ x, y, color }) => (
  <g strokeLinecap="round">
    <circle cx={x} cy={y} r="12.5" fill="#FFFFFF" stroke={color} strokeWidth="0.6" />
    <path
      d={`M ${x - 11} ${y} Q ${x} ${y - 9} ${x + 11} ${y} Q ${x} ${y + 9} ${x - 11} ${y} Z`}
      fill="none"
      stroke={color}
      strokeWidth="1.7"
    />
    <circle cx={x} cy={y} r="3.1" fill={color} stroke="none" />
  </g>
);

const Projection = ({ x, y, shape, color }) => {
  const s = 11;
  const common = { fill: "none", stroke: color, strokeWidth: 1.3, opacity: 0.85 };
  if (shape === "triangle")
    return <polygon points={`${x},${y - s} ${x + s},${y + s} ${x - s},${y + s}`} {...common} />;
  if (shape === "square")
    return <rect x={x - s} y={y - s} width={s * 2} height={s * 2} rx="2" {...common} />;
  if (shape === "circle") return <circle cx={x} cy={y} r={s} {...common} />;
  return <polygon points={`${x},${y - s} ${x + s},${y} ${x},${y + s} ${x - s},${y}`} {...common} />;
};

export const PerspectiveDiagram = () => {
  return (
    <motion.div
      data-testid="perspective-diagram"
      className="relative w-full max-w-lg mx-auto"
      initial={CONTAINER_INITIAL}
      whileInView={CONTAINER_IN_VIEW}
      viewport={CONTAINER_VIEWPORT}
      transition={CONTAINER_TRANSITION}
    >
      <svg viewBox="0 0 320 320" className="w-full h-auto">
        {/* axes + eyes + projections */}
        {povs.map((p, i) => {
          const a = (p.angle * Math.PI) / 180;
          const ex = C.x + R_EYE * Math.cos(a);
          const ey = C.y + R_EYE * Math.sin(a);
          const px = C.x + R_PROJ * Math.cos(a);
          const py = C.y + R_PROJ * Math.sin(a);
          const lineX = C.x + R_LINE * Math.cos(a);
          const lineY = C.y + R_LINE * Math.sin(a);
          return (
            <g key={p.label}>
              <motion.line
                x1={C.x}
                y1={C.y}
                x2={lineX}
                y2={lineY}
                stroke={p.color}
                strokeWidth="0.5"
                strokeDasharray="1.6 2.4"
                initial={LINE_INITIAL}
                whileInView={LINE_IN_VIEW}
                viewport={CONTAINER_VIEWPORT}
                transition={{ duration: 1, delay: 0.4 + i * 0.12, ease: "easeInOut" }}
              />
              <Projection x={px} y={py} shape={p.shape} color={p.color} />
              <Eye x={ex} y={ey} color={p.color} />
            </g>
          );
        })}

        {/* central form — one situation seen from every side */}
        <g stroke={INK} fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path
            d="M160 128 C 140 138 132 168 148 186 C 158 197 176 195 184 182 C 192 169 186 146 160 128 Z"
            strokeWidth="1.8"
          />
          <path d="M160 128 C 168 150 168 168 160 186" strokeWidth="1.2" strokeDasharray="1.6 2.4" opacity="0.6" />
          <path d="M148 186 C 160 180 172 180 184 182" strokeWidth="1.2" strokeDasharray="1.6 2.4" opacity="0.6" />
        </g>
      </svg>

      {/* labels */}      {povs.map((p) => {
        const a = (p.angle * Math.PI) / 180;
        const lx = 50 + (100 * R_LABEL * Math.cos(a)) / 320;
        const ly = 50 + (100 * R_LABEL * Math.sin(a)) / 320;
        return (
          <div
            key={p.label}
            data-testid={`pov-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${lx}%`, top: `${ly}%` }}
          >
            <div
              className="flex items-center gap-1 sm:gap-1.5 whitespace-nowrap rounded-full border bg-[#FFFFFF] px-2 py-0.5 sm:px-3 sm:py-1.5"
              style={{ borderColor: p.color }}
            >
              <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="font-sans text-[10px] sm:text-xs" style={{ color: p.color }}>
                {p.label}
              </span>
            </div>
          </div>
        );
      })}
    </motion.div>
  );
};

export default PerspectiveDiagram;
